/**
 * TextInput / Textarea — `.input` / `.textarea` from the prototype
 * (cms-redesign-mockup.html ~2936–2961).
 *
 * Shared recipe (single source of truth for both):
 *   • Cream bg, sand border, rounded-md, padding 9/12, navy 13.5px
 *   • Focus: surface bg, teal-green border, teal-soft outer ring (4px)
 *   • Width 100% so a parent FieldBlock controls its column width
 *
 * The :focus ring is rendered via inline boxShadow on focus/blur
 * handlers so we don't have to declare a custom Tailwind ring color.
 * Plain HTML controls — no MUI, no ref complications.
 *
 * Color values are referenced via CSS variables from themeTokens.css
 * (`rgb(var(--color-x))`) so the inputs follow the active theme.
 * Without this the input bg would stay light-cream in dark mode while
 * the surrounding chrome flipped to dark — the exact symptom that
 * forced this migration.
 */
import React from 'react';
import { cn } from '../cn';
import './TextInput.scss';

const BASE_STYLE = {
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 13.5,
  color:      'rgb(var(--color-navy))',                /* navy heading text */
  padding: '9px 12px',
  background: 'rgb(var(--color-cream))',               /* cream */
  border:     '1px solid rgb(var(--color-sand))',      /* sand */
  borderRadius: 8,
  outline: 'none',
  transition: 'all 160ms ease',
};

function applyFocus(e) {
  e.currentTarget.style.background  = 'rgb(var(--color-surface))';
  e.currentTarget.style.borderColor = 'rgb(var(--color-teal-green))';
  /* 4px outer halo at ~20% teal alpha. */
  e.currentTarget.style.boxShadow   = '0 0 0 4px rgb(var(--color-teal-green) / 0.2)';
}
function applyBlur(e) {
  e.currentTarget.style.background  = 'rgb(var(--color-cream))';
  e.currentTarget.style.borderColor = 'rgb(var(--color-sand))';
  e.currentTarget.style.boxShadow   = 'none';
}

export function TextInput({
  className,
  style,
  onFocus,
  onBlur,
  ...rest
}) {
  return (
    <input
      type="text"
      className={cn('redesign-input', className)}
      style={{ ...BASE_STYLE, ...style }}
      onFocus={(e) => { applyFocus(e); onFocus?.(e); }}
      onBlur={(e) => { applyBlur(e); onBlur?.(e); }}
      {...rest}
    />
  );
}

export function Textarea({
  className,
  style,
  onFocus,
  onBlur,
  ...rest
}) {
  return (
    <textarea
      className={cn('redesign-input', className)}
      style={{
        ...BASE_STYLE,
        resize: 'vertical',
        minHeight: 90,
        lineHeight: 1.55,
        ...style,
      }}
      onFocus={(e) => { applyFocus(e); onFocus?.(e); }}
      onBlur={(e) => { applyBlur(e); onBlur?.(e); }}
      {...rest}
    />
  );
}

export function Select({
  className,
  style,
  onFocus,
  onBlur,
  ...rest
}) {
  return (
    <select
      className={cn('redesign-input', className)}
      style={{
        ...BASE_STYLE,
        // Native select carries some browser default chrome; the
        // surrounding container makes it match `.input`.
        appearance: 'auto',
        ...style,
      }}
      onFocus={(e) => { applyFocus(e); onFocus?.(e); }}
      onBlur={(e) => { applyBlur(e); onBlur?.(e); }}
      {...rest}
    />
  );
}
